/** @typedef { import('rxjs').Observable } Observable */
import { ofType } from 'redux-observable';
import { merge, of } from 'rxjs';
import { catchError, filter, mergeMap, take, finalize } from 'rxjs/operators';

import { emptyArray } from 'ui-components/default-props/empty';
import { findSellerProviderIdNeedingPickupPrompt, openPickupPromptIfAppropriate } from '../../services/carrier-pickup-helper';
import { LabelBatchErrorService } from '../../services/label-batch-error-service';
import { labelBatchListener } from '../../services/pusher-service/persistent-listeners';
import { BULK_PROCESSING_CREATE_LABEL_FINAL_MESSAGE, BULK_PROCESSING_FULFILLMENT_UNKNOWN_ERROR } from '../bulk/bulk.actions';
import * as BulkApi from '../bulk/bulk.api';
import { shouldShowSingleLabelCostReview } from '../cost-review/cost-review.helpers';

export const INSUFFICIENT_BALANCE_ERROR_CODE = 'InsufficientBalance';

/**
 * @typedef {object} CreateLabelApiSuccess
 * @property {number[]} labelBatchIds
 * @property {boolean} apiSuccess
 */

/**
 * @typedef {object} LabelBatchFinalMessage
 * @property {string} finalMessage
 * @property {number[]} labelBatchIds
 */

/**
 * @typedef {object} LabelBatchError
 * @property {number[]} labelBatchIds
 * @property {string[]} friendlyErrors
 * @property {object} errorResponse
 * @property {string} errorMessage
 */
/**
 * @typedef {object} CreateLabelApiCostReviewError
 * @property {boolean} hasCostReviewError
 */

/**
 * Sets listeners on the redux action$ to listen for label batch errors and successes.
 * If the label batch succeeds this will return the success message.
 * If the label batch fails then a request will be sent to retrieve more information about the error.
 * @template T
 * @typedef {import('rxjs').Observable<T>} LabelPurchasePusherAction$
 * @param {object} params
 * @param {Observable} params.action$
 * @param {number[]} params.labelBatchIds
 * @param {(labelBatchIds: number[]) => void?} params.onFinish
 * @param {string} [params.sellerProviderIdForPickupPrompt] - a sellerProviderId that might need a pickup prompt shown
 * @returns {LabelPurchasePusherAction$<LabelBatchFinalMessage|LabelBatchError>}
 */
export const labelPurchasePusherRace = ({ action$, labelBatchIds, onFinish, sellerProviderIdForPickupPrompt = '' }) => {
  return merge(
    action$.pipe(
      ofType(BULK_PROCESSING_CREATE_LABEL_FINAL_MESSAGE),
      filter((act) => labelBatchIds.includes(act.batchId)),
      mergeMap((act) => {
        openPickupPromptIfAppropriate(sellerProviderIdForPickupPrompt);

        return of({ labelBatchIds, finalMessage: act.message });
      }),
    ),
    action$.pipe(
      ofType(BULK_PROCESSING_FULFILLMENT_UNKNOWN_ERROR),
      filter((act) => labelBatchIds.includes(act.batchId)),
      mergeMap((act) => {
        const stringyBatchId = labelBatchIds[0].toString();

        return BulkApi.getBulkCreateLabelErrors([stringyBatchId]).pipe(
          mergeMap((resp) => {
            const filteredErrors = resp.find((error) => error.labelBatchid === stringyBatchId);
            const batchItems = filteredErrors?.items || emptyArray;
            const errorItems = batchItems.filter((error) => error.status === 'Error');
            const errors = errorItems.map((error) => {
              const errorMessageDefinition = LabelBatchErrorService.getErrorMessage(error);
              return errorMessageDefinition;
            });

            return of({
              labelBatchIds,
              friendlyErrors: errors,
              errorResponse: errorItems,
              errorMessage: act.message,
            });
          }),
        );
      }),
    ),
  ).pipe(
    take(labelBatchIds.length),
    finalize(() => {
      if (onFinish) {
        onFinish(labelBatchIds);
      }
    }),
  );
};

/**
 * Make a request to the bulk api to create label(s). If the request is successful
 * then this will automatically watch for redux actions that indicate the label
 * purchase succeeded or failed. It will also set a timeout to poll AsyncNotifierService for any
 * potentially missed pusher messages.
 * The result of the api request and any sort of pusher messages
 * are returned as 'next' values of an observable.
 * @template T
 * @param {object} params
 * @param {string[]} params.fulfillmentPlanIds - consumers must ensure this has length
 * @param {Date} params.shipDate - date object
 * @param {Observable} params.action$ - redux action stream
 * @param {object?} params.logContext
 * @param {object?} params.quickshipProps
 * @param {boolean?} params.shouldThrowApiError - if true then always throw when the api request fails instead of checking for a cost review error
 * @param {() => object} params.getState
 * @typedef {import('rxjs').Observable<T>} LabelPurchase$
 * @returns {LabelPurchase$<CreateLabelApiSuccess|LabelBatchFinalMessage|LabelBatchError|CreateLabelApiCostReviewError>}
 */
export const makeBulkCreateLabelObservable = ({
  fulfillmentPlanIds,
  shipDate,
  action$,
  logContext = {},
  quickshipProps = {},
  shouldThrowApiError = true,
  getState,
}) => {
  return BulkApi.createLabel(fulfillmentPlanIds, shipDate).pipe(
    mergeMap((response) => {
      const { onStart, onFinish } = labelBatchListener.createSingleJobScopedHandlers();

      const labelBatchIds = response?.labelBatchIds || [];
      const allLogContext = {
        fulfillmentPlanId: fulfillmentPlanIds,
        labelBatchIds,
        ...logContext,
      };
      onStart(labelBatchIds, allLogContext);

      const sellerProviderIdForPickupPrompt = findSellerProviderIdNeedingPickupPrompt(getState(), fulfillmentPlanIds);

      const nextPusher$ = labelPurchasePusherRace({
        action$,
        labelBatchIds,
        onFinish,
        sellerProviderIdForPickupPrompt,
      });
      return merge(nextPusher$, of({ labelBatchIds, apiSuccess: true }));
    }),
    catchError((errorResponse) => {
      const responseData = errorResponse?.response?.data;
      if (!responseData || shouldThrowApiError) {
        // the error is not from the api response, so re-throw to avoid swallowing
        throw errorResponse;
      }

      const hasCostReviewError = shouldShowSingleLabelCostReview({
        responseData,
        quickshipProps,
      });
      if (hasCostReviewError) {
        return of({ hasCostReviewError: true });
      }
      // re-throw any non-cost review error so that the consumer can handle as needed
      throw errorResponse;
    }),
  );
};
